import { Controller } from "@hotwired/stimulus"
import { escapeHtml } from "../utils/html_helpers"
import { OnboardingApi } from "../services/onboarding_api"

// Main onboarding wizard controller - orchestrates the flow between steps
export default class extends Controller {
  static values = {
    sessionId: String,
    icpStatus: String,
    clientName: String,
    currentStep: Number,
    companyInputs: Object,
    icpInputs: Object
  }

  static targets = [
    "panel", "stepCounter", "progressBar", "phaseIndicator",
    "summaryOutput", "finalSummary",
    // Form fields for loading session data
    "companyName", "companyDomain", "companyOneliner", "companyLinkedin",
    "targetMarket", "problemsSolved",
    "businessStage", "bestCustomers",
    // Legacy field
    "companyValue"
  ]

  static outlets = [
    "onboarding--company-analysis",
    "onboarding--icp",
    "onboarding--example-mode"
  ]

  connect() {
    // State object we use for summary + payloads
    this.icpState = this.icpState || {}

    // Normalise current step
    if (!this.hasCurrentStepValue || isNaN(this.currentStepValue)) {
      this.currentStepValue = 0
    }

    // Load existing session data if available
    this.loadExistingSessionData()

    this.showCurrentStep()
    this.updateSummary()

    // Listen for events from child controllers
    this.element.addEventListener('onboarding--company-analysis:analysis-complete', (e) => {
      console.log('[wizard] Company analysis complete')
      this.updateSummary()
    })

    this.element.addEventListener('onboarding--company-analysis:summary-complete', (e) => {
      console.log('[wizard] Summary complete')
      this.updateSummary()
    })

    this.element.addEventListener('onboarding--icp:generation-complete', (e) => {
      console.log('[wizard] ICP generation complete')
      // Auto-advance to the Personas step (3) if we're on the Generate step or earlier
      if (this.currentStepValue < 3 && this.panelTargets[3]) {
        this.currentStepValue = 3
        this.showCurrentStep()
      }
    })
  }

  // --------------------------------------------------------
  // Load Existing Session Data
  // --------------------------------------------------------

  loadExistingSessionData() {
    const companyInputs = this.companyInputsValue || {}
    const icpInputs = this.icpInputsValue || {}

    // Check if we have any data to load
    const hasData = Object.keys(companyInputs).length > 0 || Object.keys(icpInputs).length > 0
    if (!hasData) return

    // Populate company basics (Step 0)
    if (this.hasCompanyNameTarget && icpInputs.company_name) {
      this.companyNameTarget.value = icpInputs.company_name
    }
    if (this.hasCompanyDomainTarget && (companyInputs.domain || icpInputs.company_domain)) {
      this.companyDomainTarget.value = companyInputs.domain || icpInputs.company_domain
    }
    if (this.hasCompanyLinkedinTarget && (companyInputs.linkedin_url || icpInputs.company_linkedin)) {
      this.companyLinkedinTarget.value = companyInputs.linkedin_url || icpInputs.company_linkedin
    }
    if (this.hasCompanyOnelinerTarget && (companyInputs.generated_summary || icpInputs.company_oneliner)) {
      this.companyOnelinerTarget.value = companyInputs.generated_summary || icpInputs.company_oneliner
    }

    // Populate what you sell (Step 1)
    if (this.hasTargetMarketTarget && icpInputs.target_market) {
      this.targetMarketTarget.value = icpInputs.target_market
    }
    if (this.hasProblemsSolvedTarget && icpInputs.problems_solved) {
      this.problemsSolvedTarget.value = icpInputs.problems_solved
    }
    if (this.hasCompanyValueTarget && icpInputs.company_value) {
      this.companyValueTarget.value = icpInputs.company_value
    }

    // Populate business stage (Step 1)
    if (icpInputs.business_stage) {
      const stageRadio = this.element.querySelector(`input[name="business_stage"][value="${icpInputs.business_stage}"]`)
      if (stageRadio) stageRadio.checked = true
    }
    if (this.hasBestCustomersTarget && icpInputs.best_customers) {
      this.bestCustomersTarget.value = icpInputs.best_customers
    }

    // Populate icpState for summary display
    this.icpState = {
      companyName: icpInputs.company_name || '',
      companyDomain: companyInputs.domain || icpInputs.company_domain || '',
      companyOneliner: companyInputs.generated_summary || icpInputs.company_oneliner || '',
      targetMarket: icpInputs.target_market || icpInputs.company_value || '',
      problemsSolved: icpInputs.problems_solved || '',
      businessStage: icpInputs.business_stage || '',
      bestCustomers: icpInputs.best_customers || '',
      website_analysis: companyInputs.website_analysis || null,
      linkedin_analysis: companyInputs.linkedin_analysis || null
    }
  }

  // --------------------------------------------------------
  // Navigation
  // --------------------------------------------------------

  next(event) {
    if (event) event.preventDefault()
    this.currentStepValue = Math.min(this.currentStepValue + 1, this.panelTargets.length - 1)
    this.showCurrentStep()
  }

  prev(event) {
    if (event) event.preventDefault()
    this.currentStepValue = Math.max(this.currentStepValue - 1, 0)
    this.showCurrentStep()
  }

  handleEnterKey(event) {
    // Only trigger on Enter key, and not when in a textarea or when a modal is open
    if (event.key !== 'Enter') return
    if (event.target.tagName === 'TEXTAREA') return
    if (document.querySelector('[data-modal]')) return
    
    // Don't trigger on the last step or during generation steps (Review & Generate onwards)
    if (this.currentStepValue >= 2) return
    
    event.preventDefault()
    this.next()
  }

  goToFirst(event) {
    if (event) event.preventDefault()
    this.currentStepValue = 0
    this.showCurrentStep()
  }

  goToStep(stepIndex) {
    if (typeof stepIndex === 'object') {
      // Called from event
      stepIndex = Number(stepIndex.currentTarget?.dataset.stepIndex)
    }
    
    if (!isNaN(stepIndex)) {
      this.currentStepValue = stepIndex
      this.showCurrentStep()
    }
  }

  goToPhase(event) {
    event.preventDefault()
    const steps = event.currentTarget.dataset.steps
    if (steps) {
      const firstStep = parseInt(steps.split(',')[0], 10)
      if (!isNaN(firstStep)) {
        this.currentStepValue = firstStep
        this.showCurrentStep()
      }
    }
  }

  // --------------------------------------------------------
  // Progress Indicators
  // --------------------------------------------------------

  updateProgressIndicators() {
    const totalSteps = this.panelTargets.length
    const currentStep = this.currentStepValue
    
    // Update step counter (e.g., "Step 3 of 8")
    if (this.hasStepCounterTarget) {
      this.stepCounterTarget.textContent = `Step ${currentStep + 1} of ${totalSteps}`
    }
    
    // Update progress bar
    if (this.hasProgressBarTarget) {
      const progress = ((currentStep + 1) / totalSteps) * 100
      this.progressBarTarget.style.width = `${progress}%`
    }
    
    // Update phase indicators
    if (this.hasPhaseIndicatorTarget) {
      const phaseMap = {
        0: 1, 1: 1,           // Steps 0-1 = Phase 1 (Company + Market)
        2: 2,                 // Step 2 = Phase 2 (Review & Generate ICP)
        3: 3, 4: 3            // Steps 3-4 = Phase 3 (Personas + Launch)
      }
      const currentPhase = phaseMap[currentStep] || 1
      
      this.phaseIndicatorTarget.querySelectorAll('.phase-pill').forEach(pill => {
        const pillPhase = parseInt(pill.dataset.phase, 10)
        
        // Remove all state classes
        pill.classList.remove(
          'border-k3c-coral', 'bg-k3c-coral/10', 'text-k3c-coral', 'phase-active',
          'border-green-500', 'bg-green-50', 'text-green-600',
          'border-theme-secondary/50', 'bg-white/50', 'text-theme-muted'
        )
        
        const numberSpan = pill.querySelector('span:first-child')
        numberSpan.classList.remove(
          'bg-k3c-coral', 'text-white', 'border-current',
          'bg-green-500', 'text-white',
          'border-2'
        )
        
        if (pillPhase < currentPhase) {
          // Completed phase
          pill.classList.add('border-green-500', 'bg-green-50', 'text-green-600')
          numberSpan.classList.add('bg-green-500', 'text-white')
          numberSpan.innerHTML = '✓'
        } else if (pillPhase === currentPhase) {
          // Current phase
          pill.classList.add('border-k3c-coral', 'bg-k3c-coral/10', 'text-k3c-coral', 'phase-active')
          numberSpan.classList.add('bg-k3c-coral', 'text-white')
          numberSpan.textContent = pillPhase
        } else {
          // Future phase
          pill.classList.add('border-theme-secondary/50', 'bg-white/50', 'text-theme-muted')
          numberSpan.classList.add('border-2', 'border-current')
          numberSpan.textContent = pillPhase
        }
      })
    }
  }

  showCurrentStep() {
    console.log('[wizard] Current step:', this.currentStepValue)
    
    // Toggle panels
    this.panelTargets.forEach((panel, idx) => {
      panel.classList.toggle("hidden", idx !== this.currentStepValue)
    })

    // Update progress indicators
    this.updateProgressIndicators()

    // Scroll to top of wizard on step change
    this.element.scrollIntoView({ behavior: 'smooth', block: 'start' })

    // Keep icpState up to date and refresh summary on every step change
    this.buildIcpPayload()
    this.updateSummary()

    // Notify outlets based on step
    const step = this.currentStepValue

    if (step === 3 && this.hasOnboardingIcpOutlet) {
      this.onboardingIcpOutlet.loadPersonas()
    }

    if (step === 4) {
      this.loadFinalSummary()
    }
  }

  // --------------------------------------------------------
  // Payload building and summary
  // --------------------------------------------------------

  buildIcpPayload() {
    const base = this.icpState || {}

    // Company basics (Step 0)
    const companyName = this.hasCompanyNameTarget ? this.companyNameTarget.value.trim() : ""
    const companyDomain = this.hasCompanyDomainTarget ? this.companyDomainTarget.value.trim() : ""
    const companyOneliner = this.hasCompanyOnelinerTarget ? this.companyOnelinerTarget.value.trim() : ""
    const companyLinkedin = this.hasCompanyLinkedinTarget ? this.companyLinkedinTarget.value.trim() : ""

    // Step 1: What you sell
    const targetMarket = this.hasTargetMarketTarget ? this.targetMarketTarget.value.trim() : ""
    const problemsSolved = this.hasProblemsSolvedTarget ? this.problemsSolvedTarget.value.trim() : ""

    // Step 1: Business stage & best customers
    const businessStageEl = this.element.querySelector('input[name="business_stage"]:checked')
    const businessStage = businessStageEl ? businessStageEl.value : ""

    const bestCustomers = this.hasBestCustomersTarget ? this.bestCustomersTarget.value.trim() : ""

    // Legacy field retained for the demo saved-sessions display and target_market fallback
    const valueProp = this.hasCompanyValueTarget ? this.companyValueTarget.value.trim() : ""

    // Get analysis data from company analysis controller if available
    let websiteAnalysis = base.website_analysis
    let linkedinAnalysis = base.linkedin_analysis
    
    if (this.hasOnboardingCompanyAnalysisOutlet) {
      const analysisData = this.onboardingCompanyAnalysisOutlet.getAnalysisData()
      if (analysisData.website) websiteAnalysis = analysisData.website
      if (analysisData.linkedin) linkedinAnalysis = analysisData.linkedin
    }

    const payload = {
      ...base,
      // New streamlined fields
      company_name: companyName,
      company_domain: companyDomain,
      company_oneliner: companyOneliner,
      company_linkedin: companyLinkedin,
      target_market: targetMarket,
      problems_solved: problemsSolved,
      business_stage: businessStage,
      best_customers: bestCustomers,
      // Website/LinkedIn analysis data
      website_analysis: websiteAnalysis,
      linkedin_analysis: linkedinAnalysis,
      // Legacy field retained for the demo saved-sessions display
      company_value: valueProp || targetMarket
    }

    this.icpState = payload
    return payload
  }

  updateSummary() {
    if (!this.hasSummaryOutputTarget) return

    const icp = this.buildIcpPayload()
    
    // Company info
    const companyName = icp.company_name || this.clientNameValue || "–"
    const domain = icp.company_domain || "–"
    const oneliner = icp.company_oneliner || "–"
    
    // What you sell
    const targetMarket = icp.target_market || "–"
    const problemsSolved = icp.problems_solved || "–"
    
    // Business stage
    const businessStage = this.labelForBusinessStage(icp.business_stage) || "–"
    const bestCustomers = icp.best_customers || ""

    // Company summary block
    let summaryBlock = ""
    const hasWebsite = icp.website_analysis && (icp.website_analysis.summary || icp.website_analysis.one_liner)
    const hasLinkedin = icp.linkedin_analysis && icp.linkedin_analysis.analysis_quality !== 'failed' && icp.linkedin_analysis.analysis_quality !== 'limited'
    
    if (oneliner && oneliner !== "–") {
      const sources = []
      if (hasWebsite) sources.push("website")
      if (hasLinkedin) sources.push("LinkedIn")
      const sourceText = sources.length > 0 ? ` (from ${sources.join(" + ")})` : ""
      
      summaryBlock = `
        <div class='mb-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800'>
          <span class='font-semibold text-green-700 dark:text-green-400 text-xs uppercase'>✓ Company Summary${sourceText}</span>
          <p class='text-sm text-theme-primary mt-1'>${escapeHtml(oneliner)}</p>
        </div>
      `
    }

    // Build summary HTML
    this.summaryOutputTargets.forEach(target => {
      target.innerHTML = `
        ${summaryBlock}
        
        <div class='grid md:grid-cols-2 gap-4'>
          <div>
            <div class='mb-3'>
              <span class='font-semibold text-theme-primary text-xs uppercase'>Company</span>
              <div class='text-theme-primary text-sm'>${escapeHtml(companyName)}</div>
              <div class='text-xs text-theme-muted'>${escapeHtml(domain)}</div>
            </div>
            
            <div class='mb-3'>
              <span class='font-semibold text-theme-primary text-xs uppercase'>Target Market</span>
              <div class='text-theme-primary text-sm whitespace-pre-wrap'>${escapeHtml(targetMarket).substring(0, 200)}${targetMarket.length > 200 ? '...' : ''}</div>
            </div>
            
            <div class='mb-3'>
              <span class='font-semibold text-theme-primary text-xs uppercase'>Problems You Solve</span>
              <div class='text-theme-primary text-sm whitespace-pre-wrap'>${escapeHtml(problemsSolved).substring(0, 200)}${problemsSolved.length > 200 ? '...' : ''}</div>
            </div>
          </div>
          
          <div>
            <div class='mb-3'>
              <span class='font-semibold text-theme-primary text-xs uppercase'>Business Stage</span>
              <div class='text-theme-primary text-sm'>${escapeHtml(businessStage)}</div>
            </div>

            ${bestCustomers ? `
            <div class='mb-3'>
              <span class='font-semibold text-theme-primary text-xs uppercase'>Best Customers</span>
              <div class='text-theme-primary text-sm'>${escapeHtml(bestCustomers)}</div>
            </div>
            ` : ""}
          </div>
        </div>
      `
    })
  }

  // Load and render the final summary for the Ready to Launch step
  async loadFinalSummary() {
    if (!this.hasFinalSummaryTarget || !this.sessionIdValue) return

    console.log('[wizard] Loading final summary')

    try {
      const icpResp = await fetch(`/onboarding/sessions/${this.sessionIdValue}/icp_status`, { headers: { Accept: "application/json" } })
      const icpData = icpResp.ok ? await icpResp.json() : {}

      const personas = icpData.personas || []

      // Count approved items
      const approvedPersonas = personas.filter(p => p.status === 'approved').length

      // Get company info from icpState
      const icp = this.buildIcpPayload()
      const companyName = icp.company_name || this.clientNameValue || "Your Company"

      // Build persona summary cards
      let personaCards = ""
      if (personas.length > 0) {
        personaCards = personas.map(p => {
          const statusColor = p.status === 'approved' ? 'bg-green-100 text-green-700 border-green-200' : 'bg-yellow-100 text-yellow-700 border-yellow-200'
          const statusLabel = p.status === 'approved' ? '✓ Approved' : 'Draft'
          const titles = Array.isArray(p.signals?.job_titles) ? p.signals.job_titles.slice(0, 3).join(', ') : ''

          return `
            <div class="border border-theme-secondary rounded-lg p-3 bg-white/50">
              <div class="flex items-start justify-between gap-2">
                <div>
                  <p class="font-semibold text-theme-primary text-sm">${escapeHtml(p.name || 'ICP')}</p>
                  <p class="text-xs text-theme-muted">${escapeHtml(p.headline || '')}</p>
                  ${titles ? `<p class="text-xs text-theme-muted mt-1">Titles: ${escapeHtml(titles)}</p>` : ''}
                </div>
                <span class="px-2 py-0.5 text-xs font-medium rounded-full border ${statusColor}">${statusLabel}</span>
              </div>
            </div>
          `
        }).join('')
      } else {
        personaCards = `<p class="text-sm text-theme-muted">No ICPs generated yet</p>`
      }

      // Render the final summary
      this.finalSummaryTarget.innerHTML = `
        <!-- Stats Overview -->
        <div class="grid grid-cols-1 gap-4 text-center">
          <div class="bg-white/70 rounded-xl p-4 border border-theme-secondary">
            <div class="text-3xl font-bold text-theme-primary">${personas.length}</div>
            <div class="text-xs text-theme-muted uppercase tracking-wide">ICPs</div>
            <div class="text-xs text-green-600 mt-1">${approvedPersonas} approved</div>
          </div>
        </div>

        <!-- Company -->
        <div class="bg-white/50 rounded-xl p-4 border border-theme-secondary">
          <p class="text-xs text-theme-muted uppercase tracking-wide">Campaign for</p>
          <p class="font-semibold text-theme-primary">${escapeHtml(companyName)}</p>
        </div>

        <!-- Personas -->
        <div>
          <h3 class="font-semibold text-theme-primary text-sm mb-2">Your Ideal Customer Profiles</h3>
          <div class="space-y-2">
            ${personaCards}
          </div>
        </div>
      `

    } catch (err) {
      console.error('[wizard] Load final summary error:', err)
      this.finalSummaryTarget.innerHTML = `
        <div class="text-center text-theme-muted py-4">
          <p>Could not load summary. Please go back and ensure your Ideal Customer Profiles are generated.</p>
        </div>
      `
    }
  }

  // Label helpers
  labelForBusinessStage(value) {
    switch (value) {
      case "finding_first":
        return "Finding first customers (0–3)"
      case "early_traction":
        return "Building early traction (3–10)"
      case "scaling":
        return "Scaling what works (10+)"
      default:
        return value || ""
    }
  }

  // Methods for example mode to call
  populateFormFields(company) {
    if (this.hasCompanyNameTarget) this.companyNameTarget.value = company.name
    if (this.hasCompanyDomainTarget) this.companyDomainTarget.value = company.domain
    if (this.hasCompanyOnelinerTarget) this.companyOnelinerTarget.value = company.oneliner
    if (this.hasTargetMarketTarget) this.targetMarketTarget.value = company.targetMarket
    if (this.hasProblemsSolvedTarget) this.problemsSolvedTarget.value = company.problemsSolved
    if (this.hasBestCustomersTarget) this.bestCustomersTarget.value = company.bestCustomers

    // Set radio buttons
    document.querySelectorAll('input[name="business_stage"]').forEach(radio => {
      radio.checked = radio.value === company.businessStage
    })

    // Update icpState
    this.icpState = {
      company_name: company.name,
      domain: company.domain,
      company_oneliner: company.oneliner,
      target_market: company.targetMarket,
      problems_solved: company.problemsSolved,
      business_stage: company.businessStage,
      best_customers: company.bestCustomers,
      is_example: true
    }

    this.updateSummary()
  }

  clearFormFields() {
    if (this.hasCompanyNameTarget) this.companyNameTarget.value = ""
    if (this.hasCompanyDomainTarget) this.companyDomainTarget.value = ""
    if (this.hasCompanyOnelinerTarget) this.companyOnelinerTarget.value = ""
    if (this.hasTargetMarketTarget) this.targetMarketTarget.value = ""
    if (this.hasProblemsSolvedTarget) this.problemsSolvedTarget.value = ""
    if (this.hasBestCustomersTarget) this.bestCustomersTarget.value = ""

    // Reset radio buttons
    document.querySelectorAll('input[name="business_stage"]').forEach(radio => {
      radio.checked = false
    })

    // Clear icpState
    this.icpState = {}
  }

  async launchCampaign(event) {
    if (event) event.preventDefault()

    const button = event?.currentTarget
    if (button) {
      button.disabled = true
      button.textContent = "Launching..."
    }

    try {
      const api = new OnboardingApi(this.sessionIdValue)
      await api.launch(this.buildIcpPayload())

      if (button) {
        button.textContent = "✓ Launched!"
        button.classList.remove("btn-theme-primary")
        button.classList.add("bg-green-500", "text-white")
      }

      setTimeout(() => {
        window.location.href = "/"
      }, 1500)
    } catch (error) {
      console.error("[wizard] Launch error:", error)
      if (button) {
        button.disabled = false
        button.textContent = "🚀 Approve & Launch"
      }
      alert("Something went wrong. Please try again.")
    }
  }
}
